import { ArrowDown, ArrowUp, ArrowUpDown, Layers, PieChart } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import type { SectorValuationPoint } from '../types';
import { cn, formatNum, sectorName } from '../utils/formatters';
import { EtfHoldingsPopover } from './EtfHoldingsPopover';

interface Props {
    data: SectorValuationPoint[] | undefined;
}

// Which numeric column the table is currently sorted by (null = original order).
type SortKey = 'pe' | 'eps';
type SortDir = 'asc' | 'desc';

// Signals whether the ETF trades above / below its 200-day moving average --
// a classic long-term trend filter. Green above, rose below.
const trendColor = (price50: number | null, price200: number | null): string => {
    if (price50 == null || price200 == null) return 'text-slate-500 dark:text-slate-400';
    return price50 >= price200
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-rose-600 dark:text-rose-400';
};

// A single sortable numeric column header. Clicking cycles asc -> desc -> off.
const SortableHeader: React.FC<{
    label: string;
    columnKey: SortKey;
    sortKey: SortKey | null;
    sortDir: SortDir;
    onSort: (key: SortKey) => void;
}> = ({ label, columnKey, sortKey, sortDir, onSort }) => {
    const isActive = sortKey === columnKey;
    return (
        <th className="px-4 py-3 font-bold text-right">
            <button
                type="button"
                onClick={() => onSort(columnKey)}
                title={`Sort by ${label} (${isActive && sortDir === 'asc' ? 'descending' : 'ascending'})`}
                className={cn(
                    'inline-flex items-center gap-1 ml-auto uppercase tracking-wider transition-colors',
                    isActive
                        ? 'text-indigo-600 dark:text-indigo-400'
                        : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300',
                )}
            >
                {label}
                {isActive ? (
                    sortDir === 'asc' ? (
                        <ArrowUp className="w-3 h-3" />
                    ) : (
                        <ArrowDown className="w-3 h-3" />
                    )
                ) : (
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                )}
            </button>
        </th>
    );
};

export const SectorValuationTable: React.FC<Props> = ({ data }) => {
    // Which ETF's holdings popover is currently open (null = none).
    const [openTicker, setOpenTicker] = useState<string | null>(null);
    // Active sort column + direction (null key preserves the backend ordering).
    const [sortKey, setSortKey] = useState<SortKey | null>(null);
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            // Toggle asc <-> desc on the already-active column.
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const baseRows = data ?? [];
    const rows = useMemo(() => {
        if (!sortKey) return baseRows;
        // Copy before sorting so we never mutate the query cache array. Nulls
        // always sink to the bottom regardless of direction.
        return [...baseRows].sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return sortDir === 'asc' ? av - bv : bv - av;
        });
    }, [baseRows, sortKey, sortDir]);

    const activeRow = rows.find((r) => r.ticker === openTicker) ?? null;

    return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 p-5 border-b border-slate-100 dark:border-slate-800">
                <div className="p-2 bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 rounded-lg">
                    <Layers className="w-5 h-5" />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                        Sector Valuations
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Discover current ETF Sector fundamentals + trend analysis: 50D versus 200D Moving Averages.
                    </p>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                {rows.length === 0 ? (
                    <div className="flex min-h-[200px] items-center justify-center text-slate-400 text-sm font-medium">
                        No valuation data available.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-800">
                                <th className="px-4 py-3 font-bold">ETF</th>
                                <SortableHeader
                                    label="P/E"
                                    columnKey="pe"
                                    sortKey={sortKey}
                                    sortDir={sortDir}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label="EPS"
                                    columnKey="eps"
                                    sortKey={sortKey}
                                    sortDir={sortDir}
                                    onSort={handleSort}
                                />
                                <th className="px-4 py-3 font-bold text-right">50D MA</th>
                                <th className="px-4 py-3 font-bold text-right">200D MA</th>
                                <th className="px-4 py-3 font-bold text-center">Holdings</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const holdingsCount = r.top_holdings?.length ?? 0;
                                return (
                                    <tr
                                        key={r.ticker}
                                        className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                                    >
                                        <td className="px-4 py-3">
                                            <Link
                                                to={`/dashboard/etf-info-page?symbol=${encodeURIComponent(r.ticker)}`}
                                                title={`Open ETF deep dive for ${r.ticker}`}
                                                className="group flex flex-col"
                                            >
                                                <span className="font-bold text-slate-900 dark:text-slate-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 group-hover:underline underline-offset-2 transition-colors">
                                                    {r.ticker}
                                                </span>
                                                {sectorName(r.ticker) && (
                                                    <span className="text-xs font-medium text-slate-400 dark:text-slate-500">
                                                        {sectorName(r.ticker)}
                                                    </span>
                                                )}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono text-slate-700 dark:text-slate-200">
                                            {formatNum(r.pe)}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono text-slate-700 dark:text-slate-200">
                                            {formatNum(r.eps)}
                                        </td>
                                        <td
                                            className={cn(
                                                'px-4 py-3 text-right font-mono font-semibold',
                                                trendColor(r.price_avg_50, r.price_avg_200),
                                            )}
                                        >
                                            {formatNum(r.price_avg_50)}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono text-slate-600 dark:text-slate-300">
                                            {formatNum(r.price_avg_200)}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button
                                                onClick={() => setOpenTicker(r.ticker)}
                                                disabled={holdingsCount === 0}
                                                className={cn(
                                                    'inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-md border transition-colors',
                                                    holdingsCount === 0
                                                        ? 'text-slate-300 border-slate-100 cursor-not-allowed dark:text-slate-600 dark:border-slate-800'
                                                        : 'text-indigo-600 border-indigo-200 hover:bg-indigo-50 dark:text-indigo-400 dark:border-indigo-800 dark:hover:bg-indigo-900/20',
                                                )}
                                            >
                                                <PieChart className="w-3.5 h-3.5" />
                                                {holdingsCount > 0 ? `Top ${holdingsCount}` : 'N/A'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Holdings drill-down popover (uses inline data, no extra fetch) */}
            {activeRow && (
                <EtfHoldingsPopover
                    ticker={activeRow.ticker}
                    holdings={activeRow.top_holdings}
                    onClose={() => setOpenTicker(null)}
                />
            )}
        </div>
    );
};

export default SectorValuationTable;
